// BillingPage — /account/billing
//
// Single source of truth for "what am I paying for and how do I stop paying."
// Design philosophy: the cancel button is a big red obvious button. No buried
// links, no multi-step "tell us why you're leaving" funnel. One click to
// cancel, one confirm, done. Stripe's hosted portal is offered separately for
// payment-method updates and invoice downloads.

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useSubscription } from "../hooks/useSubscription";

// Workaround classes for the themed cancel / resume / primary buttons.
// The rules they're guarding against are `button { background: #111827 }` in
// src/styles.css (no !important) and `.tattoo-app button { ... }` in
// tattoo-ui.css (scoped to the editor, doesn't reach this page). Class
// specificity (0,0,1,0) already beats element specificity (0,0,0,1), so the
// !important markers below are technically redundant — kept defensively until
// a visual sweep confirms they can be dropped across all themed pages.
const billingButtonStyles = `
.tt-billing-btn-danger {
  background: #dc2626 !important;
  color: #ffffff !important;
}
.tt-billing-btn-danger:hover:not(:disabled) {
  background: #ef4444 !important;
}
.tt-billing-btn-danger:disabled {
  background: #8a1d18 !important;
  cursor: not-allowed !important;
}
.tt-billing-btn-success {
  background: #10b981 !important;
  color: #0a0a0a !important;
}
.tt-billing-btn-success:hover:not(:disabled) {
  background: #34d399 !important;
}
.tt-billing-btn-success:disabled {
  background: #0d8a64 !important;
  cursor: not-allowed !important;
}
.tt-billing-btn-primary {
  background: #a2332b !important;
  color: #ffffff !important;
}
.tt-billing-btn-primary:hover:not(:disabled) {
  background: #c2403a !important;
}
.tt-billing-btn-primary:disabled {
  background: #7a1f1a !important;
  cursor: not-allowed !important;
}
`;
import { PLANS, ARTIST_TRIAL_DAYS, TRIAL_DAYS } from "./constants";
import {
  openBillingPortal,
  cancelSubscription,
  resumeSubscription,
  startCheckout,
} from "./api";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function planDisplayName(planKey) {
  return PLANS[planKey]?.name || planKey || "Free";
}

function planPriceLabel(planKey) {
  const p = PLANS[planKey];
  if (!p) return "";
  return `${p.price}${p.cadence}`;
}

// Monthly ↔ yearly swap targets within the same product tier. Returns null
// if the current plan has no swap counterpart (legacy/unknown plans, or
// single_design which is a one-off purchase, not a subscription).
//
// Cross-tier swaps (Customer Pro → Artist Pro) are intentionally omitted —
// Artist Pro requires a shop profile, so it's a sign-up flow, not a one-click
// plan switch.
const SWAP_TARGETS = {
  customer_pro: "customer_pro_yearly",
  customer_pro_yearly: "customer_pro",
  artist_pro: "artist_pro_yearly",
  artist_pro_yearly: "artist_pro",
};
function swapTargetFor(planKey) {
  return SWAP_TARGETS[planKey] || null;
}

export default function BillingPage() {
  const navigate = useNavigate();
  const sub = useSubscription();

  const {
    loading,
    tier,
    status,
    subscription,
    trialDaysLeft,
    artistTrialStarted,
    artistTrialDaysLeft,
    artistTrialActive,
    refresh,
  } = sub;

  // TEMP diagnostic — remove once Billing page is verified end-to-end.
  useEffect(() => {
    if (loading) return;
    console.log("[BillingPage] state", {
      tier,
      status,
      trialDaysLeft,
      artistTrialStarted,
      artistTrialDaysLeft,
      artistTrialActive,
      subscription,
    });
  }, [
    loading,
    tier,
    status,
    trialDaysLeft,
    artistTrialStarted,
    artistTrialDaysLeft,
    artistTrialActive,
    subscription,
  ]);

  const [busy, setBusy] = useState(null); // "cancel" | "resume" | "portal" | null

  if (loading) {
    return (
      <PageShell onBack={() => navigate("/app")}>
        <p style={{ color: "#a8a8a8" }}>Loading your billing details…</p>
      </PageShell>
    );
  }

  const cancelPending = !!subscription?.cancel_at_period_end;
  const isPaying =
    !!subscription &&
    ["active", "trialing", "past_due"].includes(subscription.status);

  async function handleCancel() {
    if (busy) return;
    const endsOn = formatDate(subscription?.current_period_end);
    const planName = planDisplayName(subscription?.plan);
    const ok = window.confirm(
      `Cancel your Tattune ${planName} subscription?\n\n` +
        `You'll keep access until ${endsOn}. After that your account drops back to free.\n\n` +
        `You can resume anytime before then.`
    );
    if (!ok) return;

    setBusy("cancel");
    try {
      await cancelSubscription();
      toast.success(`Subscription canceled. Access continues until ${endsOn}.`, {
        duration: 5500,
      });
      await refresh();
    } catch (err) {
      toast.error(err.message || "Could not cancel — please try again.");
    } finally {
      setBusy(null);
    }
  }

  async function handleResume() {
    if (busy) return;
    setBusy("resume");
    try {
      await resumeSubscription();
      toast.success("Welcome back — your subscription will keep renewing.", {
        duration: 5000,
      });
      await refresh();
    } catch (err) {
      toast.error(err.message || "Could not resume — please try again.");
    } finally {
      setBusy(null);
    }
  }

  async function handlePortal() {
    if (busy) return;
    setBusy("portal");
    try {
      await openBillingPortal();
      // openBillingPortal navigates away; if we land here it failed.
    } catch (err) {
      toast.error(err.message || "Could not open billing portal.");
      setBusy(null);
    }
  }

  async function handleUpgrade(plan) {
    if (busy) return;
    setBusy("upgrade");
    try {
      const result = await startCheckout(plan);
      // From FreeOrEndedView the user has no active sub, so we expect the
      // redirect path. But if the Edge Function ever returns a swap/no-op
      // outcome (e.g. webhook lag race), handle it gracefully.
      if (result?.swapped) {
        toast.success(
          `You're now on ${planDisplayName(result.plan)}. Stripe will prorate the difference on your next invoice.`,
          { duration: 5500 }
        );
        await refresh();
        setBusy(null);
      } else if (result?.alreadyOnPlan) {
        toast.success("You're already on that plan.");
        await refresh();
        setBusy(null);
      }
      // result.redirected: page is navigating away, don't clear busy.
    } catch (err) {
      if (err.code === "already_subscribed") {
        // Legacy path — older Edge Function deploy. Refresh and let the
        // page swap to ActiveSubscriptionView.
        toast.success("You already have an active subscription.");
        await refresh();
      } else {
        toast.error(err.message || "Could not open checkout.");
      }
      setBusy(null);
    }
  }

  async function handleSwapPlan(plan) {
    if (busy) return;
    const fromName = planDisplayName(subscription?.plan);
    const toName = planDisplayName(plan);
    const toPrice = planPriceLabel(plan);
    const ok = window.confirm(
      `Switch from ${fromName} to ${toName} (${toPrice})?\n\n` +
        `Stripe will automatically prorate the difference — you'll be ` +
        `charged or credited for the change based on the time remaining ` +
        `in your current billing period.`
    );
    if (!ok) return;

    setBusy("swap");
    try {
      const result = await startCheckout(plan);
      if (result?.swapped) {
        toast.success(
          `Switched to ${planDisplayName(result.plan)}. Stripe will prorate the difference on your next invoice.`,
          { duration: 5500 }
        );
        await refresh();
      } else if (result?.alreadyOnPlan) {
        toast.success("You're already on that plan.");
        await refresh();
      } else if (result?.redirected) {
        // Unexpected — we have an active sub, so the Edge Function should
        // not have created a new Checkout session. If it did, the redirect
        // already fired and we won't reach here.
        console.warn("[billing] swap unexpectedly redirected to Checkout");
      }
    } catch (err) {
      toast.error(err.message || "Could not switch plan — please try again.");
    } finally {
      setBusy(null);
    }
  }

  // -------- copy / state strings --------
  const planName = planDisplayName(subscription?.plan ?? tier);
  const priceLabel = subscription?.plan ? planPriceLabel(subscription.plan) : "";

  let statusLine;
  if (cancelPending) {
    statusLine = (
      <span style={{ color: "#f9a826" }}>
        Active — ends on {formatDate(subscription.current_period_end)}
      </span>
    );
  } else if (subscription?.status === "active") {
    statusLine = (
      <span style={{ color: "#10b981" }}>
        Renews on {formatDate(subscription.current_period_end)}
      </span>
    );
  } else if (subscription?.status === "trialing") {
    statusLine = (
      <span style={{ color: "#10b981" }}>
        Trialing — first charge {formatDate(subscription.current_period_end)}
      </span>
    );
  } else if (subscription?.status === "past_due") {
    statusLine = (
      <span style={{ color: "#ef4444" }}>
        Payment failed — please update your card to keep access.
      </span>
    );
  } else if (subscription?.status === "canceled") {
    statusLine = (
      <span style={{ color: "#a8a8a8" }}>
        Canceled on {formatDate(subscription.current_period_end)}
      </span>
    );
  }

  return (
    <PageShell onBack={() => navigate("/app")}>
      {isPaying ? (
        <ActiveSubscriptionView
          planName={planName}
          priceLabel={priceLabel}
          statusLine={statusLine}
          cancelPending={cancelPending}
          busy={busy}
          currentPlanKey={subscription?.plan}
          onCancel={handleCancel}
          onResume={handleResume}
          onPortal={handlePortal}
          onSwapPlan={handleSwapPlan}
        />
      ) : (
        <FreeOrEndedView
          status={subscription?.status ?? null}
          tier={tier}
          customerTrialDaysLeft={trialDaysLeft}
          artistTrialStarted={artistTrialStarted}
          artistTrialDaysLeft={artistTrialDaysLeft}
          artistTrialActive={artistTrialActive}
          busy={busy}
          onUpgrade={handleUpgrade}
        />
      )}

      <ArtistTrialFootnote
        artistTrialStarted={artistTrialStarted}
        artistTrialDaysLeft={artistTrialDaysLeft}
        artistTrialActive={artistTrialActive}
      />
    </PageShell>
  );
}

// ------------------- subcomponents -------------------

function PageShell({ children, onBack }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#0a0a0a",
        color: "#eaeaea",
        padding: "32px 20px 80px",
      }}
    >
      <style>{billingButtonStyles}</style>
      <div style={{ maxWidth: 640, margin: "0 auto" }}>
        <button
          type="button"
          onClick={onBack}
          style={{
            background: "transparent",
            border: "1px solid #2a2a2a",
            color: "#d7d7d7",
            padding: "6px 12px",
            borderRadius: 8,
            cursor: "pointer",
            fontSize: 13,
            marginBottom: 24,
          }}
        >
          ← Back to app
        </button>

        <h1 style={{ fontSize: 24, margin: "0 0 6px", letterSpacing: "0.02em" }}>
          Billing
        </h1>
        <p style={{ fontSize: 13, color: "#a8a8a8", margin: "0 0 24px" }}>
          Manage your Tattune subscription.
        </p>

        {children}
      </div>
    </div>
  );
}

function ActiveSubscriptionView({
  planName,
  priceLabel,
  statusLine,
  cancelPending,
  busy,
  currentPlanKey,
  onCancel,
  onResume,
  onPortal,
  onSwapPlan,
}) {
  const swapTarget = swapTargetFor(currentPlanKey);
  return (
    <>
      <Card>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: "0.08em" }}>
              Current plan
            </div>
            <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}>
              {planName}
            </div>
            {priceLabel && (
              <div style={{ fontSize: 14, color: "#a8a8a8", marginTop: 2 }}>
                {priceLabel}
              </div>
            )}
          </div>
        </div>

        <div style={{ marginTop: 16, fontSize: 14, color: "#cfcfcf" }}>
          {statusLine}
        </div>
      </Card>

      {/* Cancel / Resume — the prominent button */}
      <div style={{ marginTop: 24 }}>
        {cancelPending ? (
          <button
            type="button"
            className="tt-billing-btn-success"
            onClick={onResume}
            disabled={!!busy}
            style={{
              width: "100%",
              padding: "16px 18px",
              border: "none",
              borderRadius: 12,
              fontSize: 16,
              fontWeight: 700,
              cursor: busy ? "not-allowed" : "pointer",
              letterSpacing: "0.02em",
              boxShadow: "0 4px 14px rgba(16, 185, 129, 0.2)",
            }}
          >
            {busy === "resume" ? "Resuming…" : "Resume subscription"}
          </button>
        ) : (
          <button
            type="button"
            className="tt-billing-btn-danger"
            onClick={onCancel}
            disabled={!!busy}
            style={{
              width: "100%",
              padding: "16px 18px",
              border: "none",
              borderRadius: 12,
              fontSize: 16,
              fontWeight: 700,
              cursor: busy ? "not-allowed" : "pointer",
              letterSpacing: "0.02em",
              boxShadow: "0 4px 14px rgba(220, 38, 38, 0.25)",
            }}
          >
            {busy === "cancel" ? "Canceling…" : "Cancel subscription"}
          </button>
        )}
        <p
          style={{
            fontSize: 12,
            color: "#888",
            marginTop: 8,
            textAlign: "center",
            lineHeight: 1.4,
          }}
        >
          {cancelPending
            ? "Your subscription is set to end. Click resume anytime to keep going."
            : "You'll keep access until the end of your current billing period. No questions, no friction."}
        </p>
      </div>

      {/* Switch plan — only shown if there's a swap counterpart (monthly ↔ yearly
          within the same product tier). Cross-tier swaps (Customer ↔ Artist)
          intentionally not offered here — Artist Pro needs a shop profile. */}
      {swapTarget && (
        <SwitchPlanPanel
          swapTarget={swapTarget}
          busy={busy}
          cancelPending={cancelPending}
          onSwapPlan={onSwapPlan}
        />
      )}

      {/* Secondary actions */}
      <div style={{ marginTop: 32, paddingTop: 24, borderTop: "1px solid #1f1f1f" }}>
        <h3 style={{ fontSize: 13, color: "#888", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 12px" }}>
          Other actions
        </h3>
        <button
          type="button"
          onClick={onPortal}
          disabled={!!busy}
          style={{
            width: "100%",
            padding: "10px 14px",
            border: "1px solid #2a2a2a",
            borderRadius: 10,
            background: "transparent",
            color: "#d7d7d7",
            fontSize: 14,
            cursor: busy ? "not-allowed" : "pointer",
          }}
        >
          {busy === "portal" ? "Opening Stripe…" : "Manage payment & invoices →"}
        </button>
        <p style={{ fontSize: 12, color: "#666", marginTop: 6, textAlign: "center" }}>
          Opens Stripe to update your card, view invoices, or change billing email.
        </p>
      </div>
    </>
  );
}

function SwitchPlanPanel({ swapTarget, busy, cancelPending, onSwapPlan }) {
  const target = PLANS[swapTarget];
  if (!target) return null;

  // Frame the swap as "Save N%" / "Go monthly" depending on direction.
  const isToYearly = swapTarget.endsWith("_yearly");
  const headline = isToYearly
    ? `Switch to yearly — ${target.name}`
    : `Switch to monthly — ${target.name}`;
  const subhead = isToYearly
    ? "Pay for a year up front. Stripe will prorate the credit from your current monthly plan."
    : "Switch to a monthly cadence. Stripe will credit the unused portion of your yearly plan.";

  return (
    <div style={{ marginTop: 32, paddingTop: 24, borderTop: "1px solid #1f1f1f" }}>
      <h3
        style={{
          fontSize: 13,
          color: "#888",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          margin: "0 0 12px",
        }}
      >
        Switch your plan
      </h3>

      <div
        style={{
          background: "#0f0f0f",
          border: "1px solid #1f1f1f",
          borderRadius: 12,
          padding: "16px 18px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: 12,
            flexWrap: "wrap",
            marginBottom: 4,
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 600, color: "#f0f0f0" }}>
            {headline}
          </div>
          <div style={{ fontSize: 14, color: "#cfcfcf" }}>
            {target.price}
            <span style={{ color: "#888" }}>{target.cadence}</span>
          </div>
        </div>
        <p style={{ fontSize: 13, color: "#a8a8a8", margin: "0 0 14px", lineHeight: 1.5 }}>
          {subhead}
        </p>

        {cancelPending && (
          <div
            style={{
              fontSize: 12,
              color: "#f9a826",
              background: "rgba(249, 168, 38, 0.08)",
              border: "1px solid rgba(249, 168, 38, 0.25)",
              borderRadius: 8,
              padding: "8px 10px",
              marginBottom: 12,
              lineHeight: 1.5,
            }}
          >
            Switching will also resume your subscription — you won't lose
            access at the end of your current period.
          </div>
        )}

        <button
          type="button"
          className="tt-billing-btn-primary"
          onClick={() => onSwapPlan(swapTarget)}
          disabled={!!busy}
          style={{
            width: "100%",
            padding: "10px 14px",
            border: "none",
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 600,
            cursor: busy ? "not-allowed" : "pointer",
          }}
        >
          {busy === "swap" ? "Switching…" : `Switch to ${target.name} →`}
        </button>
      </div>
    </div>
  );
}

function FreeOrEndedView({
  status,
  customerTrialDaysLeft,
  artistTrialActive,
  artistTrialDaysLeft,
  busy,
  onUpgrade,
}) {
  // Decide what to show as the "current plan" header.
  // Artist trial wins over customer trial (it's the "stronger" entitlement —
  // includes everything customer_pro has plus profile/flash/leads).
  let planLabel = "Free";
  let planSubtext;
  if (artistTrialActive) {
    planLabel = "Artist Pro · Trial";
    planSubtext = `${artistTrialDaysLeft} day${artistTrialDaysLeft === 1 ? "" : "s"} left on your ${ARTIST_TRIAL_DAYS}-day artist trial. Subscribe before it ends to keep your profile, flash uploads, and lead inbox.`;
  } else if (customerTrialDaysLeft > 0) {
    planLabel = "Customer Pro · Trial";
    planSubtext = `${customerTrialDaysLeft} day${customerTrialDaysLeft === 1 ? "" : "s"} left on your ${TRIAL_DAYS}-day free trial.`;
  } else if (status === "canceled") {
    planSubtext = "Your previous subscription has ended.";
  } else {
    planSubtext = "No active subscription.";
  }

  return (
    <>
      <Card>
        <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: "0.08em" }}>
          Current plan
        </div>
        <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}>
          {planLabel}
        </div>
        <div style={{ fontSize: 14, color: "#a8a8a8", marginTop: 4, lineHeight: 1.5 }}>
          {planSubtext}
        </div>
      </Card>

      <div style={{ marginTop: 24, display: "grid", gap: 12 }}>
        <button
          type="button"
          className="tt-billing-btn-primary"
          onClick={() => onUpgrade("customer_pro")}
          disabled={!!busy}
          style={{
            width: "100%",
            padding: "16px 18px",
            border: "none",
            borderRadius: 12,
            fontSize: 16,
            fontWeight: 700,
            cursor: busy ? "not-allowed" : "pointer",
            letterSpacing: "0.02em",
          }}
        >
          {busy === "upgrade" ? "Opening checkout…" : `Subscribe to Customer Pro — ${PLANS.customer_pro.price}${PLANS.customer_pro.cadence}`}
        </button>

        <button
          type="button"
          onClick={() => onUpgrade("artist_pro")}
          disabled={!!busy}
          style={{
            width: "100%",
            padding: "12px 16px",
            border: "1px solid #2a2a2a",
            borderRadius: 10,
            background: "transparent",
            color: "#d7d7d7",
            fontSize: 14,
            cursor: busy ? "not-allowed" : "pointer",
          }}
        >
          Subscribe as an Artist — {PLANS.artist_pro.price}{PLANS.artist_pro.cadence}
        </button>
      </div>
    </>
  );
}

function ArtistTrialFootnote({
  artistTrialStarted,
  artistTrialDaysLeft,
  artistTrialActive,
}) {
  if (!artistTrialStarted) return null;

  return (
    <div
      style={{
        marginTop: 28,
        paddingTop: 20,
        borderTop: "1px solid #1f1f1f",
        fontSize: 13,
        color: "#a8a8a8",
        lineHeight: 1.5,
      }}
    >
      <strong style={{ color: "#d7d7d7", fontWeight: 600 }}>Artist trial:</strong>{" "}
      {artistTrialActive
        ? `${artistTrialDaysLeft} day${artistTrialDaysLeft === 1 ? "" : "s"} of your ${ARTIST_TRIAL_DAYS}-day trial remaining.`
        : `Your ${ARTIST_TRIAL_DAYS}-day artist trial has ended. Subscribe to Artist Pro to keep your profile, flash uploads, and lead inbox.`}
    </div>
  );
}

function Card({ children }) {
  return (
    <div
      style={{
        background: "#111111",
        border: "1px solid #1f1f1f",
        borderRadius: 14,
        padding: "20px 22px",
      }}
    >
      {children}
    </div>
  );
}
